import { DialogManager } from '@/components/DialogManager'
import { DialogLoadingFallback } from '@/components/DialogManager/DialogLoadingFallback'
import { Header } from '@/components/Header'
import { Footer } from '@/components/Footer'
import { LegalRibbon } from '@/components/Footer/LegalRibbon'
import { AnalyticsRouteTracker } from '@/components/GoogleAnalytics/AnalyticsRouteTracker'
import { ClarityIdentityTracker } from '@/components/Clarity/ClarityIdentityTracker'
import { appInsights, reactPlugin } from '@/services/telemetry/appInsights'
import type { RouterContext } from '@/types'
import { RegistrationStatus } from '@/types/'
import { checkRegistrationStatus } from '@/services/authentication/checkRegistrationStatus'
import {
  createRootRouteWithContext,
  isRedirect,
  Outlet,
  useMatchRoute,
  redirect,
} from '@tanstack/react-router'
import { AppInsightsErrorBoundary } from '@microsoft/applicationinsights-react-js'
import { Suspense, useLayoutEffect, useState } from 'react'
import { registrationEmailAtom } from '@/state/global'
import {
  initialWorkOrderIdAtom,
  emailLinkWorkOrderIdAtom,
} from '@/state/authentication/login'
import { configureJotaiStore } from '@/state'
import { getSearchParam } from '@/helpers/url-search-params'
import { useHideOnScroll } from '@/hooks/useHideOnScroll'
import { ClarityEvent, trackClarityEvent } from '@/services/telemetry/clarity'

export function ErrorFallback() {
  return (
    <div className="p-8 text-center">
      <h1 className="text-2xl font-bold mb-2">Something went wrong</h1>
      <p className="text-gray-600">
        An unexpected error occurred. Please try again.
      </p>
    </div>
  )
}

const RootLayout = () => {
  const routesWithHeader = ['/dashboard', '/verifyInfo', '/readOnly']
  const routesWithLegalRibbon = [
    '/dashboard',
    '/register',
    '/verifyInfo',
    '/readOnly',
  ]
  const routesWithFooter = [
    '/dashboard',
    '/verifyInfo',
    '/register',
    '/readOnly',
  ]
  const matchRoute = useMatchRoute()
  const hideOnScroll = useHideOnScroll()

  const [footerNode, setFooterNode] = useState<HTMLDivElement | null>(null)
  const [footerHeight, setFooterHeight] = useState(0)

  useLayoutEffect(() => {
    if (!footerNode) return

    const observer = new ResizeObserver(([entry]) => {
      setFooterHeight(entry.contentRect.height)
    })
    observer.observe(footerNode)
    return () => observer.disconnect()
  }, [footerNode])

  const [ribbonNode, setRibbonNode] = useState<HTMLDivElement | null>(null)
  const [ribbonHeight, setRibbonHeight] = useState(0)

  useLayoutEffect(() => {
    if (!ribbonNode) return

    const observer = new ResizeObserver(([entry]) => {
      setRibbonHeight(entry.contentRect.height)
    })
    observer.observe(ribbonNode)
    return () => observer.disconnect()
  }, [ribbonNode])

  const isHeaderVisible = routesWithHeader.some((route) =>
    matchRoute({ to: route }),
  )
  const isLegalRibbonVisible = routesWithLegalRibbon.some((route) =>
    matchRoute({ to: route }),
  )

  const isFooterVisible = routesWithFooter.some((route) =>
    matchRoute({ to: route }),
  )

  const inner = (
    <div className={isLegalRibbonVisible ? 'pb-24' : 'pb-12'}>
      {isHeaderVisible && <Header hidden={hideOnScroll} />}
      <Outlet />
      <AnalyticsRouteTracker />
      <ClarityIdentityTracker />
      <Suspense fallback={<DialogLoadingFallback />}>
        <DialogManager />
      </Suspense>
      {isLegalRibbonVisible && (
        <div
          ref={setRibbonNode}
          style={{
            // Sits directly above the footer when shown; since the footer
            // now slides away too instead of staying put, this needs to
            // slide fully off on its own rather than tuck behind it.
            bottom: hideOnScroll ? -ribbonHeight : footerHeight,
            transition: 'bottom 300ms ease-in-out',
          }}
          className="fixed left-0 z-40 w-full"
          // Same issue as Header: moving this off-screen doesn't remove
          // its Terms/Privacy links from the tab order or the a11y tree
          // on its own.
          aria-hidden={hideOnScroll}
          inert={hideOnScroll}
        >
          <LegalRibbon />
        </div>
      )}
      {isFooterVisible && (
        <div
          ref={setFooterNode}
          style={{
            bottom: hideOnScroll ? -footerHeight : 0,
            transition: 'bottom 300ms ease-in-out',
          }}
          className="fixed left-0 z-50 w-full"
          aria-hidden={hideOnScroll}
          inert={hideOnScroll}
        >
          <Footer />
        </div>
      )}
    </div>
  )

  if (appInsights) {
    return (
      <AppInsightsErrorBoundary
        onError={() => <ErrorFallback />}
        appInsights={reactPlugin}
      >
        {inner}
      </AppInsightsErrorBoundary>
    )
  }

  return inner
}

export const Route = createRootRouteWithContext<RouterContext>()({
  beforeLoad: async ({ location }) => {
    const encryptedUrl = getSearchParam(location.search, 'EmailRequest')

    if (encryptedUrl) {
      try {
        const response = await checkRegistrationStatus(encryptedUrl)

        if (response.isSuccess) {
          const linkWorkOrderId =
            (response.data?.workOrderId ?? 0) > 0
              ? response.data!.workOrderId!
              : null
          // Persist the email-link WO independently so login can't overwrite it
          configureJotaiStore().set(emailLinkWorkOrderIdAtom, linkWorkOrderId)
          // Also prime initialWorkOrderIdAtom so it's correct before the user logs in
          configureJotaiStore().set(initialWorkOrderIdAtom, null)
          if (linkWorkOrderId !== null) {
            configureJotaiStore().set(initialWorkOrderIdAtom, linkWorkOrderId)
          }

          if (
            response.registrationStatus === RegistrationStatus.NotRegistered ||
            (response.registrationStatus ===
              RegistrationStatus.PasswordResetRequested &&
              (response.data?.emailAddress ?? '') !== '')
          ) {
            configureJotaiStore().set(
              registrationEmailAtom,
              response.data?.emailAddress ?? '',
            )
            if (
              response.registrationStatus === RegistrationStatus.NotRegistered
            ) {
              throw redirect({ to: '/register' })
            }
            throw redirect({ to: '/resetPassword' })
          }
          if (
            response.registrationStatus ===
            RegistrationStatus.RegisteredNotVerified
          ) {
            trackClarityEvent(ClarityEvent.VerificationRequired)
            throw redirect({ to: '/verifyInfo' })
          }
          if (
            response.registrationStatus ===
            RegistrationStatus.RegisteredVerified
          ) {
            trackClarityEvent(ClarityEvent.VerificationNotRequired)
            throw redirect({ to: '/dashboard' })
          }
          throw redirect({ to: '/login' })
        } else {
          throw redirect({ to: '/login' })
        }
      } catch (_err) {
        if (isRedirect(_err)) throw _err
        throw redirect({ to: '/login' })
      }
    }
  },
  component: RootLayout,
})
